import {
	maskIdentifier,
	SENSITIVE_IDENTIFIER_KEYS,
} from '@dealstack/frontend/shared/utils/bankAccountDisplay.utils.ts';
import { BankAccountRes } from '@dealstack/shared/lib/generated';
import { NAME_IDENTIFIER_KEYS } from '@dealstack/shared/lib/common/business/bankAccountIdentifiers';

/**
 * Whatever identifies the account, preferring a sensitive field over a public one.
 * Legacy rows predate the identifier model, so they fall back to the account's own
 * columns under the key the masker knows them by.
 */
const findIdentifyingField = (account: BankAccountRes): { key: string; value: string } | null => {
	const identifier =
		account.identifiers.find((row) => SENSITIVE_IDENTIFIER_KEYS.has(row.key)) ??
		account.identifiers.find((row) => !NAME_IDENTIFIER_KEYS.has(row.key));

	if (identifier) {
		return { key: identifier.key, value: identifier.value };
	}
	if (account.accountNumber) {
		return { key: 'account_number', value: account.accountNumber };
	}
	if (account.iban) {
		return { key: 'iban', value: account.iban };
	}

	return null;
};

/**
 * One line for the account picker.
 *
 * Every classic field is optional under the identifier model: a bank name only exists once
 * a lookup succeeded, and a format-rule account (sort code, PIX, CLABE) may carry neither
 * `bankName` nor `accountNumber` — the backend resolves those to `''`. Naming them directly
 * renders an option reading " ()", so this falls through to whatever actually identifies
 * the account and drops the parenthesis when nothing does.
 */
export const getBankAccountOptionLabel = (account: BankAccountRes): string => {
	const name = account.bankName || account.accountHolder || '';
	const identifying = findIdentifyingField(account);
	const shown = identifying ? maskIdentifier(identifying.key, identifying.value) : '';

	if (!name) {
		// Nothing to name it by — an account entered before bank name was captured.
		return shown || account.id;
	}

	return shown ? `${name} (${shown})` : name;
};
